var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, IconButton, MenuItem, Switch, TextField, Tooltip, Typography, } from '@mui/material';
import { Scrollbar } from 'app/components';
import { prefixCreate } from 'app/store/retransmission-prefix';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getApiErrorMessage, PREFIX_NAME_MAX_LENGTH, validateDestinationUrl, validatePrefixName } from './prefixNameRules';
import { PREFIX_FIELD_HINTS } from './retransmissionPrefixHints';
import { buildPrefixPayload, isWialonD } from './retransmissionPrefixPayload';
import TransportGroupsAutocomplete from './TransportGroupsAutocomplete';
const EMPTY_FORM = {
    prefix: '',
    protocol: '',
    type: null,
    url: '',
    transportGroups: [],
    active: false,
    sendSensors: false,
    sendAlarms: false,
    sendGuards: false,
};
const PROTOCOLS = ['XML', 'WIALON', 'EGTS', 'POST'];
const RetransmissionSettingsDialog = (props) => {
    const { open, onClose, onCreated, groupItems } = props;
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const wialonD = isWialonD(formData.protocol, formData.type);
    const handleChange = (field) => (event) => {
        const value = event.target.value;
        if (field === 'protocol') {
            setFormData(Object.assign(Object.assign({}, formData), { protocol: value, type: value === 'WIALON' ? 'SD' : null, sendSensors: false, sendAlarms: false, sendGuards: false }));
        }
        else if (field === 'type') {
            const nextType = value || null;
            const nextWialonD = isWialonD(formData.protocol, nextType);
            setFormData(Object.assign(Object.assign({}, formData), { type: nextType, sendSensors: nextWialonD ? formData.sendSensors : false, sendAlarms: nextWialonD ? formData.sendAlarms : false, sendGuards: nextWialonD ? formData.sendGuards : false }));
        }
        else {
            setFormData(Object.assign(Object.assign({}, formData), { [field]: value }));
        }
    };
    const handleFlagChange = (field) => (event) => {
        setFormData(Object.assign(Object.assign({}, formData), { [field]: event.target.checked }));
    };
    const handleFormSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        setError(null);
        const prefix = (_a = formData.prefix) === null || _a === void 0 ? void 0 : _a.trim();
        const url = (_b = formData.url) === null || _b === void 0 ? void 0 : _b.trim();
        const protocol = formData.protocol;
        const prefixError = validatePrefixName(prefix);
        if (prefixError) {
            setError(prefixError);
            return;
        }
        if (!protocol || !PROTOCOLS.includes(protocol)) {
            setError('Выберите протокол');
            return;
        }
        const urlError = validateDestinationUrl(url);
        if (urlError) {
            setError(urlError);
            return;
        }
        if (protocol === 'WIALON' && formData.type !== 'SD' && formData.type !== 'D') {
            setError('Для WIALON выберите тип SD или D');
            return;
        }
        setIsLoading(true);
        try {
            const result = yield dispatch(prefixCreate(buildPrefixPayload(Object.assign(Object.assign({}, formData), { prefix,
                url, groups: formData.transportGroups, send_sensors: formData.sendSensors, send_alarms: formData.sendAlarms, send_guards: formData.sendGuards, active: formData.active }))));
            if (result === null || result === void 0 ? void 0 : result.success) {
                yield (onCreated === null || onCreated === void 0 ? void 0 : onCreated());
                handleCancel();
            }
            else {
                setError(getApiErrorMessage(result === null || result === void 0 ? void 0 : result.error, 'Не удалось создать префикс'));
            }
        }
        catch (err) {
            setError(getApiErrorMessage(err, 'Произошла ошибка при создании префикса'));
        }
        finally {
            setIsLoading(false);
        }
    });
    const handleCancel = () => {
        setFormData(EMPTY_FORM);
        setError(null);
        onClose();
    };
    useEffect(() => {
        if (open) {
            setFormData(EMPTY_FORM);
            setError(null);
        }
    }, [open]);
    const isTypeDisabled = formData.protocol !== 'WIALON';
    return (React.createElement(Dialog, { maxWidth: 'lg', open: open, onClose: onClose, sx: (theme) => ({
            width: '100%',
            height: '100%',
            '.MuiDialog-paper': {
                minWidth: '540px',
            },
            '.MuiDialogTitle-root': {
                display: 'flex',
                padding: theme.spacing(2),
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.spacing(1),
            },
            '.MuiDialogContent-root': {
                padding: theme.spacing(0),
            },
            '.MuiDialogContent-root .simplebar-container': {
                padding: theme.spacing(0, 2),
            },
            '.MuiDialogActions-root': {
                padding: theme.spacing(2),
                justifyContent: 'flex-end',
                gap: theme.spacing(1),
            },
            '.MuiAlert-root': {
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(2),
                borderRadius: theme.shape.borderRadius,
            },
        }) },
        React.createElement(DialogTitle, null,
            React.createElement(Typography, { component: 'span', variant: 'h6' }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043F\u0440\u0435\u0444\u0438\u043A\u0441 \u0440\u0435\u0442\u0440\u0430\u043D\u0441\u043B\u044F\u0446\u0438\u0438"),
            React.createElement(IconButton, { color: 'default', onClick: handleCancel, "aria-label": '\u0417\u0430\u043A\u0440\u044B\u0442\u044C' },
                React.createElement(Close, { fontSize: 'small' }))),
        React.createElement(DialogContent, null,
            React.createElement(Scrollbar, null,
                React.createElement(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 3, mt: 2 } }, "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043F\u0430\u0440\u0430\u043C\u0435\u0442\u0440\u044B \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u0438 \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u0435 \u043F\u0440\u0435\u0444\u0438\u043A\u0441."),
                React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: formData.active, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { active: e.target.checked })), size: 'small' }), label: formData.active ? 'Префикс активен' : 'Префикс выключен', sx: { mb: 0.5, display: 'flex', ml: 0 } }),
                React.createElement(Typography, { variant: 'caption', color: 'text.secondary', sx: { display: 'block', mb: 2 } }, PREFIX_FIELD_HINTS.active),
                React.createElement(TextField, { fullWidth: true, size: 'small', label: '\u041F\u0440\u0435\u0444\u0438\u043A\u0441', placeholder: '\u041D\u0430\u043F\u0440\u0438\u043C\u0435\u0440, LUK', helperText: PREFIX_FIELD_HINTS.prefix, value: formData.prefix, onChange: handleChange('prefix'), inputProps: { maxLength: PREFIX_NAME_MAX_LENGTH }, sx: { mb: 2 } }),
                React.createElement("div", { style: { display: 'flex', gap: '16px', marginBottom: '8px' } },
                    React.createElement(TextField, { select: true, size: 'small', label: '\u041F\u0440\u043E\u0442\u043E\u043A\u043E\u043B', helperText: PREFIX_FIELD_HINTS.protocol, value: formData.protocol, onChange: handleChange('protocol'), sx: { flex: 1 } },
                        React.createElement(MenuItem, { value: '', disabled: true }, "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043F\u0440\u043E\u0442\u043E\u043A\u043E\u043B"),
                        React.createElement(MenuItem, { value: 'XML' }, "XML"),
                        React.createElement(MenuItem, { value: 'WIALON' }, "WIALON"),
                        React.createElement(MenuItem, { value: 'EGTS' }, "EGTS"),
                        React.createElement(MenuItem, { value: 'POST' }, "POST")),
                    React.createElement(TextField, { select: true, size: 'small', label: '\u0422\u0438\u043F', helperText: PREFIX_FIELD_HINTS.type, value: formData.type || '', onChange: handleChange('type'), disabled: isTypeDisabled, sx: {
                            flex: 1,
                            '& .MuiOutlinedInput-root.Mui-disabled': {
                                backgroundColor: '#f5f5f5',
                            },
                        } },
                        React.createElement(MenuItem, { value: 'SD' }, "SD"),
                        React.createElement(MenuItem, { value: 'D' }, "D"))),
                React.createElement(TextField, { fullWidth: true, size: 'small', label: '\u041A\u0443\u0434\u0430', placeholder: formData.protocol === 'WIALON' ? 'host:20332' : 'https://host/path или host:port', helperText: PREFIX_FIELD_HINTS.url, value: formData.url, onChange: handleChange('url'), inputProps: { maxLength: 500 }, sx: { mb: 2, mt: 1 } }),
                wialonD && (React.createElement(FormGroup, { sx: { mb: 2 } },
                    React.createElement(Typography, { variant: 'caption', color: 'text.secondary', sx: { mb: 1 } }, PREFIX_FIELD_HINTS.dFlags),
                    React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: formData.sendSensors, onChange: handleFlagChange('sendSensors'), size: 'small' }), label: React.createElement(Tooltip, { title: PREFIX_FIELD_HINTS.sendSensors },
                            React.createElement("span", null, "\u0414\u0430\u0442\u0447\u0438\u043A\u0438 \u0414\u0423\u0422/\u0414\u0423\u041D (dtN, duN, duk)")) }),
                    React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: formData.sendGuards, onChange: handleFlagChange('sendGuards'), size: 'small' }), label: React.createElement(Tooltip, { title: PREFIX_FIELD_HINTS.sendGuards },
                            React.createElement("span", null, "\u041E\u0445\u0440\u0430\u043D\u0430 (grd)")) }),
                    React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: formData.sendAlarms, onChange: handleFlagChange('sendAlarms'), size: 'small' }), label: React.createElement(Tooltip, { title: PREFIX_FIELD_HINTS.sendAlarms },
                            React.createElement("span", null, "\u0422\u0440\u0435\u0432\u043E\u0433\u0430 (alrm)")) }))),
                React.createElement(TransportGroupsAutocomplete, { options: groupItems || [], value: formData.transportGroups || [], onChange: (groups) => setFormData(Object.assign(Object.assign({}, formData), { transportGroups: groups })), label: '\u0422\u0440\u0430\u043D\u0441\u043F\u043E\u0440\u0442\u043D\u044B\u0435 \u0433\u0440\u0443\u043F\u043F\u044B', placeholder: '\u041F\u043E\u0438\u0441\u043A \u0433\u0440\u0443\u043F\u043F\u044B\u2026', helperText: PREFIX_FIELD_HINTS.groups, size: 'small', sx: { mb: 2 } }),
                error && React.createElement(Alert, { severity: 'error' }, error))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { variant: 'outlined', onClick: handleCancel, disabled: isLoading }, "\u041E\u0442\u043C\u0435\u043D\u0430"),
            React.createElement(LoadingButton, { variant: 'contained', loading: isLoading, onClick: handleFormSubmit, disableElevation: true }, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043F\u0440\u0435\u0444\u0438\u043A\u0441"))));
};
export default RetransmissionSettingsDialog;
